import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { fmtEUR, categoryLabel, CATEGORIES } from "@/lib/constants";
import { LayoutDashboard, User, Briefcase, FileText, ClipboardList, FolderCheck, FileSignature, MessagesSquare } from "lucide-react";
import { toast } from "sonner";
import CandidateDocuments from "@/components/CandidateDocuments";
import ProfilePhoto from "@/components/ProfilePhoto";
import CandidateContracts from "@/components/CandidateContracts";
import DangerZone from "@/components/DangerZone";
import ContactAdminButton from "@/components/ContactAdminButton";
import { CandidateTimesheetModal } from "@/components/TimeSheetModals";
import ExpiringDocsBanner from "@/components/ExpiringDocsBanner";
import MissionChat from "@/components/MissionChat";
import CandidateAgenda from "@/components/CandidateAgenda";
import MissionLiveTracking from "@/components/MissionLiveTracking";
import MissionsMap from "@/components/MissionsMap";
import { SuperTalentBadge } from "@/components/CandidateBadges";
import CandidateGuidedTour, { fireConfetti } from "@/components/CandidateGuidedTour";
import ConnectStatusBanner from "@/components/ConnectStatusBanner";
import { LayoutGrid, Map as MapIcon } from "lucide-react";
import ClientEvaluationModal from "@/components/ClientEvaluationModal";

const NAV = [
    { to: "/candidat", label: "Tableau de bord", icon: LayoutDashboard, end: true },
    { to: "/candidat/profil", label: "Mon profil", icon: User },
    { to: "/candidat/documents", label: "Mes documents", icon: FolderCheck },
    { to: "/candidat/contrats", label: "Mes contrats", icon: FileSignature },
    { to: "/candidat/missions", label: "Missions disponibles", icon: Briefcase },
    { to: "/candidat/candidatures", label: "Mes candidatures", icon: ClipboardList },
];

export default function CandidateDashboard() {
    const nav = useNavigate();
    const [tab, setTab] = useState("dashboard");
    const [profile, setProfile] = useState(null);
    const [missions, setMissions] = useState([]);
    const [apps, setApps] = useState([]);
    const [unreadChat, setUnreadChat] = useState(0);
    const [tourOpen, setTourOpen] = useState(false);
    const [prevAppsCount, setPrevAppsCount] = useState(null);

    const refresh = async () => {
        try {
            const p = await api.get("/candidates/me");
            setProfile(p.data);
            // Feb 2026 — force onboarding wizard if profile still incomplete and not submitted
            if (
                p.data &&
                p.data.profile_status === "incomplete" &&
                !p.data.submitted_for_review_at
            ) {
                nav("/candidat/onboarding", { replace: true });
                return;
            }
            // Auto-open guided tour when profile becomes validated (once per session)
            if (
                p.data &&
                ["validated", "premium"].includes(p.data.profile_status) &&
                !sessionStorage.getItem("renforteo_tour_seen")
            ) {
                setTourOpen(true);
            }
        } catch (_) {}
        try {
            const m = await api.get("/missions/available");
            setMissions(m.data);
        } catch (_) {}
        try {
            const a = await api.get("/applications/mine");
            // Fire confetti when candidate submits their VERY first application
            if (
                prevAppsCount === 0 &&
                a.data.length === 1 &&
                !sessionStorage.getItem("renforteo_first_apply")
            ) {
                fireConfetti();
                sessionStorage.setItem("renforteo_first_apply", "1");
            }
            setPrevAppsCount(a.data.length);
            setApps(a.data);
        } catch (_) {}
        try {
            const u = await api.get("/chat/unread-count");
            setUnreadChat(u.data?.count || 0);
        } catch (_) {}
    };

    useEffect(() => {
        refresh();
        const iv = setInterval(async () => {
            try {
                const u = await api.get("/chat/unread-count");
                setUnreadChat(u.data?.count || 0);
            } catch (_) {}
        }, 20000);
        return () => clearInterval(iv);
    }, []);

    return (
        <DashboardShell title="Espace candidat" nav={NAV}>
            <div className="mb-8 flex items-center gap-5 flex-wrap">
                <ProfilePhoto candidateUserId={profile?.user_id} editable size={96} />
                <div className="flex-1">
                    <div className="overline mb-2">Bonjour {profile?.first_name}</div>
                    <h1 className="font-display text-3xl md:text-4xl">Votre espace professionnel.</h1>
                </div>
                <ContactAdminButton />
            </div>

            <ExpiringDocsBanner />

            <ConnectStatusBanner profileStatus={profile?.profile_status} />

            {/* Profile status */}
            <div className="sd-card p-5 mb-6 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-sm text-slate-500">Statut du profil</div>
                    <div className="font-display text-xl flex items-center gap-2 flex-wrap">
                        <ProfileStatus s={profile?.profile_status} />
                        {profile?.is_super_talent && <SuperTalentBadge />}
                    </div>
                </div>
                <button
                    onClick={() => setTab("profile")}
                    className="btn-navy text-sm"
                    data-testid="edit-profile-btn"
                >
                    Compléter mon profil
                </button>
            </div>

            {profile && ["validated", "premium"].includes(profile.profile_status) && !profile.is_super_talent && (
                <div
                    className="mb-6 rounded-xl border border-[#F5A623]/40 bg-[#F5A623]/10 p-4 flex items-start gap-3"
                    data-testid="super-talent-hint"
                >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F5A623] to-[#f7b74a] text-[#0B192C] font-black flex items-center justify-center shrink-0">
                        ★
                    </div>
                    <div className="text-sm text-slate-700 leading-relaxed">
                        <div className="font-semibold text-[#0B192C] mb-1">
                            Devenez « Super Talent » et prenez la tête des recherches
                        </div>
                        Obtenez ce badge doré dès que vous atteignez <b>10 missions terminées</b>{" "}
                        avec une <b>note moyenne ≥ 4,7/5</b> et <b>zéro no-show</b>.
                        Vous apparaîtrez alors en priorité auprès des clients et pourrez
                        accéder aux missions urgentes en avant-première. Vous en êtes à{" "}
                        <b>{profile.completed_missions || 0} mission(s)</b> terminée(s) ·{" "}
                        note {profile.rating != null ? `${profile.rating}/5` : "—"} ·{" "}
                        {profile.no_shows || 0} no-show.
                    </div>
                </div>
            )}

            {profile && !["validated", "premium"].includes(profile.profile_status) && (
                <div
                    className="mb-6 rounded-xl border-l-4 border-amber-400 bg-amber-50 p-4 flex items-start gap-3"
                    data-testid="pending-validation-banner"
                >
                    <div className="w-9 h-9 rounded-full bg-amber-400 text-white font-black flex items-center justify-center shrink-0">!</div>
                    <div className="text-sm text-slate-700 leading-relaxed">
                        <div className="font-semibold text-[#0B192C] mb-1">
                            Votre profil n'est pas encore validé
                        </div>
                        Vous ne pourrez postuler à aucune mission tant que notre équipe n'a pas
                        contrôlé vos documents obligatoires : <b>SIRET, KBIS, carte d'identité et
                        justificatif de domicile</b>. Téléversez-les dans <button
                            onClick={() => setTab("documents")}
                            className="text-[#0B192C] font-semibold underline hover:text-[#F5A623]"
                            data-testid="pending-goto-docs-btn"
                        >Mes documents</button>.
                        Vous recevrez un e-mail dès que votre profil sera validé.
                    </div>
                </div>
            )}

            <div className="flex gap-2 mb-6 flex-wrap">
                <TabBtn active={tab === "dashboard"} onClick={() => setTab("dashboard")} label="Aperçu" />
                <TabBtn active={tab === "profile"} onClick={() => setTab("profile")} label="Mon profil" testId="tab-profile" />
                <TabBtn active={tab === "documents"} onClick={() => setTab("documents")} label="Mes documents" testId="tab-documents" />
                <TabBtn active={tab === "contracts"} onClick={() => setTab("contracts")} label="Mes contrats" testId="tab-contracts" />
                <TabBtn active={tab === "missions"} onClick={() => setTab("missions")} label={`Missions (${missions.length})`} testId="tab-missions" />
                <TabBtn active={tab === "applications"} onClick={() => setTab("applications")} label={`Mes candidatures (${apps.length})`} testId="tab-applications" />
                <TabBtn active={tab === "agenda"} onClick={() => setTab("agenda")} label="Agenda" testId="tab-agenda" />
                <TabBtn active={tab === "invoices"} onClick={() => setTab("invoices")} label="Factures Renforteo" testId="tab-invoices" />
                <TabBtn
                    active={tab === "chat"}
                    onClick={() => setTab("chat")}
                    label={
                        <span className="inline-flex items-center gap-2">
                            <MessagesSquare size={14} /> Messagerie
                            {unreadChat > 0 && (
                                <span
                                    className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold animate-pulse"
                                    data-testid="candidate-chat-badge"
                                >
                                    {unreadChat}
                                </span>
                            )}
                        </span>
                    }
                    testId="tab-chat"
                />
            </div>

            {tab === "dashboard" && (
                <div>
                    <div className="grid md:grid-cols-3 gap-4">
                        <KPI label="Missions disponibles" value={missions.length} />
                        <KPI label="Mes candidatures" value={apps.length} />
                        <KPI label="Sélections" value={apps.filter((a) => a.status === "selected").length} />
                    </div>
                    <PendingClientEvaluations />
                </div>
            )}

            {tab === "profile" && profile && (
                <ProfileForm profile={profile} onSave={refresh} />
            )}

            {tab === "documents" && (
                <CandidateDocuments
                    profileStatus={profile?.profile_status}
                />
            )}

            {tab === "contracts" && (
                <CandidateContracts firstName={profile?.first_name} />
            )}

            {tab === "missions" && (
                <MissionsList missions={missions} onApply={refresh} profile={profile} />
            )}

            {tab === "applications" && (
                <CandidateApplicationsPanel apps={apps} onChange={refresh} />
            )}

            {tab === "agenda" && (
                <CandidateAgenda />
            )}

            {tab === "invoices" && (
                <CandidateInvoicesPanel />
            )}

            <div className="mt-10">
                <DangerZone />
            </div>
            <CandidateGuidedTour
                open={tourOpen}
                onClose={() => setTourOpen(false)}
                onGoto={(target) => {
                    if (target === "missions") setTab("missions");
                }}
            />
        </DashboardShell>
    );
}

function TabBtn({ active, onClick, label, testId }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                active
                    ? "bg-[#0B192C] text-white border-[#0B192C]"
                    : "bg-white border-slate-200 hover:border-[#0B192C]/30"
            }`}
        >
            {label}
        </button>
    );
}

function KPI({ label, value }) {
    return (
        <div className="sd-card p-5">
            <div className="overline mb-2">{label}</div>
            <div className="kpi-value">{value}</div>
        </div>
    );
}

function PendingClientEvaluations() {
    const [items, setItems] = useState([]);
    const [current, setCurrent] = useState(null);
    const refresh = () => {
        api.get("/client-ratings/pending")
            .then((r) => setItems(r.data))
            .catch(() => setItems([]));
    };
    useEffect(() => { refresh(); }, []);
    if (items.length === 0) return null;
    return (
        <section
            className="sd-card p-6 mt-8 border-l-4 border-[#F5A623]"
            data-testid="pending-client-evaluations"
        >
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                <div>
                    <div className="overline text-[#F5A623]">Notation client</div>
                    <h2 className="font-display text-xl">
                        {items.length} client{items.length > 1 ? "s" : ""} à noter
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">
                        Aidez la communauté Renforteo : donnez votre retour sur l'accueil, le brief, les horaires et le paiement.
                    </p>
                </div>
            </div>
            <div className="divide-y divide-slate-100">
                {items.map((it) => (
                    <div
                        key={it.application_id}
                        className="py-3 flex justify-between items-center gap-3 flex-wrap"
                        data-testid={`pending-client-eval-${it.application_id}`}
                    >
                        <div>
                            <div className="font-semibold text-sm">
                                {it.client?.company_name || it.client?.full_name || "Client"}
                            </div>
                            <div className="text-xs text-slate-500">
                                {it.mission_title} · {it.mission_date} · {it.mission_city}
                            </div>
                        </div>
                        <button
                            onClick={() => setCurrent(it)}
                            className="btn-gold text-xs"
                            data-testid={`open-client-eval-${it.application_id}`}
                        >
                            Noter le client
                        </button>
                    </div>
                ))}
            </div>
            <ClientEvaluationModal
                open={!!current}
                pending={current}
                onClose={() => setCurrent(null)}
                onSubmitted={refresh}
            />
        </section>
    );
}

function ProfileStatus({ s }) {
    const map = {
        incomplete: ["Incomplet", "pill pill-red"],
        pending_verification: ["En attente de vérification", "pill pill-gold"],
        docs_needed: ["Documents à compléter", "pill pill-gold"],
        interview: ["Entretien à prévoir", "pill pill-gold"],
        validated: ["Profil validé", "pill pill-green"],
        premium: ["Profil premium", "pill pill-green"],
        suspended: ["Profil suspendu", "pill pill-red"],
        refused: ["Refusé", "pill pill-red"],
    };
    const [label, cls] = map[s] || ["Incomplet", "pill pill-red"];
    return <span className={cls}>{label}</span>;
}

function ProfileForm({ profile, onSave }) {
    const [f, setF] = useState(profile);
    const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
    const [saving, setSaving] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/candidates/me", f);
            toast.success("Profil mis à jour");
            onSave();
        } catch (err) {
            toast.error("Erreur");
        } finally {
            setSaving(false);
        }
    };
    const setLang = (i, key, v) => {
        const langs = [...(f.languages || [])];
        langs[i] = { ...langs[i], [key]: v };
        set("languages", langs);
    };
    const addLang = () => set("languages", [...(f.languages || []), { lang: "", level: "" }]);
    const isSec = f.primary_job === "agent_securite";

    return (
        <form onSubmit={submit} className="sd-card p-6 grid md:grid-cols-2 gap-4">
            <div>
                <label className="block text-sm font-semibold mb-1">Prénom</label>
                <input value={f.first_name || ""} onChange={(e) => set("first_name", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white" />
            </div>
            <div>
                <label className="block text-sm font-semibold mb-1">Nom</label>
                <input value={f.last_name || ""} onChange={(e) => set("last_name", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white" />
            </div>
            <div>
                <label className="block text-sm font-semibold mb-1">Téléphone</label>
                <input value={f.phone || ""} onChange={(e) => set("phone", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white" />
            </div>
            <div>
                <label className="block text-sm font-semibold mb-1">Ville</label>
                <input value={f.city || ""} onChange={(e) => set("city", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white" />
            </div>
            <div>
                <label className="block text-sm font-semibold mb-1">Métier principal *</label>
                <select value={f.primary_job || ""} onChange={(e) => set("primary_job", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white"
                    data-testid="profile-primary-job">
                    <option value="">— Sélectionner —</option>
                    {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
            </div>
            <div>
                <label className="block text-sm font-semibold mb-1">Années d'expérience</label>
                <input type="number" min={0} value={f.experience_years || 0}
                    onChange={(e) => set("experience_years", parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white" />
            </div>
            <div>
                <label className="block text-sm font-semibold mb-1">Statut professionnel</label>
                <div className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-600"
                     data-testid="profile-professional-status-locked">
                    Auto-entrepreneur / indépendant
                    <span className="block text-[11px] text-slate-400 mt-0.5">
                        Statut requis pour intervenir sur Renforteo.
                    </span>
                </div>
            </div>
            <div className="md:col-span-2">
                <label className="block text-sm font-semibold mb-2">
                    Métiers secondaires <span className="text-xs text-slate-400 font-normal">(cochez tous ceux où vous justifiez d'une expérience)</span>
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="secondary-jobs-grid">
                    {CATEGORIES.filter((c) => c.key !== f.primary_job).map((c) => {
                        const checked = (f.secondary_jobs || []).includes(c.key);
                        return (
                            <label key={c.key}
                                className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer text-xs ${
                                    checked ? "border-[#F5A623] bg-amber-50" : "border-slate-200 bg-white hover:border-slate-300"
                                }`}>
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                        const cur = new Set(f.secondary_jobs || []);
                                        if (e.target.checked) cur.add(c.key);
                                        else {
                                            cur.delete(c.key);
                                            const exps = { ...(f.job_experiences || {}) };
                                            delete exps[c.key];
                                            set("job_experiences", exps);
                                        }
                                        set("secondary_jobs", Array.from(cur));
                                    }}
                                    className="mt-0.5 accent-[#F5A623]"
                                    data-testid={`secondary-job-${c.key}`}
                                />
                                <span>{c.label}</span>
                            </label>
                        );
                    })}
                </div>
            </div>
            {(f.primary_job || (f.secondary_jobs || []).length > 0) && (
                <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-2">
                        Expérience par métier
                        <span className="text-xs text-slate-400 font-normal ml-1">(quelques mots pour justifier — visible par l'admin)</span>
                    </label>
                    <div className="space-y-2">
                        {[f.primary_job, ...(f.secondary_jobs || [])].filter(Boolean).map((k) => {
                            const cat = CATEGORIES.find((c) => c.key === k);
                            if (!cat) return null;
                            return (
                                <div key={k} className="grid md:grid-cols-[220px_1fr] gap-2 items-center">
                                    <div className="text-xs font-semibold text-[#0B192C]">
                                        {k === f.primary_job && <span className="pill pill-gold text-[10px] mr-1">principal</span>}
                                        {cat.label}
                                    </div>
                                    <input
                                        type="text"
                                        value={(f.job_experiences || {})[k] || ""}
                                        onChange={(e) => {
                                            const exps = { ...(f.job_experiences || {}) };
                                            exps[k] = e.target.value;
                                            set("job_experiences", exps);
                                        }}
                                        placeholder="Ex : 3 ans chez Sofitel, saisons été 2022-2024"
                                        className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                                        data-testid={`job-experience-${k}`}
                                        maxLength={300}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            <div>
                <label className="block text-sm font-semibold mb-1">Mobilité (km)</label>
                <input type="number" value={f.mobility_km || 20}
                    onChange={(e) => set("mobility_km", parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white" />
            </div>
            <div className="md:col-span-2">
                <label className="block text-sm font-semibold mb-1">Présentation / Bio</label>
                <textarea rows={3} value={f.bio || ""} onChange={(e) => set("bio", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white" />
            </div>
            <div className="md:col-span-2">
                <div className="flex justify-between mb-2">
                    <label className="text-sm font-semibold">Langues parlées</label>
                    <button type="button" onClick={addLang} className="text-xs text-[#0B192C] font-semibold">
                        + Ajouter
                    </button>
                </div>
                <div className="space-y-2">
                    {(f.languages || []).map((l, i) => (
                        <div key={i} className="grid grid-cols-2 gap-2">
                            <input placeholder="Langue" value={l.lang || ""} onChange={(e) => setLang(i, "lang", e.target.value)}
                                className="px-3 py-2 rounded-lg border border-slate-200 bg-white" />
                            <input placeholder="Niveau" value={l.level || ""} onChange={(e) => setLang(i, "level", e.target.value)}
                                className="px-3 py-2 rounded-lg border border-slate-200 bg-white" />
                        </div>
                    ))}
                </div>
            </div>
            {false && isSec && (
                <>
                    {/* Bloc CNAPS retiré au lancement — plateforme ne commercialise pas la sécurité privée. Conservé désactivé pour compatibilité DB. */}
                </>
            )}
            <div className="md:col-span-2">
                <button className="btn-gold w-full" disabled={saving} data-testid="save-profile-btn">
                    {saving ? "Enregistrement..." : "Enregistrer mon profil"}
                </button>
            </div>
        </form>
    );
}

function MissionsList({ missions, onApply, profile }) {
    const [filterCat, setFilterCat] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [urgentOnly, setUrgentOnly] = useState(false);
    const [view, setView] = useState("list");
    const [filtered, setFiltered] = useState(missions);
    const [loading, setLoading] = useState(false);

    React.useEffect(() => {
        setFiltered(missions);
    }, [missions]);

    const applyFilters = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filterCat) params.job_category = filterCat;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            if (urgentOnly) params.urgent_only = true;
            const r = await api.get("/missions/available", { params });
            setFiltered(r.data);
        } catch (_) {
            toast.error("Impossible d'appliquer les filtres");
        } finally {
            setLoading(false);
        }
    };
    const resetFilters = () => {
        setFilterCat("");
        setDateFrom("");
        setDateTo("");
        setUrgentOnly(false);
        setFiltered(missions);
    };

    return (
        <div className="space-y-4">
            {/* Filter bar */}
            <div className="sd-card p-4 grid gap-3 md:grid-cols-[1fr_1fr_1fr_auto_auto] items-end">
                <div>
                    <label className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1">Métier</label>
                    <select
                        value={filterCat}
                        onChange={(e) => setFilterCat(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                        data-testid="missions-filter-cat"
                    >
                        <option value="">Tous mes métiers</option>
                        {CATEGORIES.map((c) => (
                            <option key={c.key} value={c.key}>{c.label}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1">À partir du</label>
                    <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                        data-testid="missions-filter-from"
                    />
                </div>
                <div>
                    <label className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1">Jusqu'au</label>
                    <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                        data-testid="missions-filter-to"
                    />
                </div>
                <label className="flex items-center gap-2 text-xs font-medium text-slate-600 select-none">
                    <input
                        type="checkbox"
                        checked={urgentOnly}
                        onChange={(e) => setUrgentOnly(e.target.checked)}
                        data-testid="missions-filter-urgent"
                    />
                    Urgentes uniquement
                </label>
                <div className="flex gap-2">
                    <button
                        onClick={applyFilters}
                        disabled={loading}
                        className="btn-navy text-sm disabled:opacity-50"
                        data-testid="missions-filter-apply"
                    >
                        {loading ? "…" : "Filtrer"}
                    </button>
                    <button
                        onClick={resetFilters}
                        className="btn-ghost-navy text-sm"
                        data-testid="missions-filter-reset"
                    >
                        Réinitialiser
                    </button>
                </div>
            </div>

            <div className="text-xs text-slate-500 flex items-center justify-between gap-3 flex-wrap">
                <span data-testid="missions-count">
                    {filtered.length} mission{filtered.length > 1 ? "s" : ""} trouvée{filtered.length > 1 ? "s" : ""}
                </span>
                <div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5">
                    <button
                        onClick={() => setView("list")}
                        className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors flex items-center gap-1 ${view === "list" ? "bg-[#0B192C] text-white" : "text-slate-500"}`}
                        data-testid="missions-view-list"
                    >
                        <LayoutGrid size={12} /> Liste
                    </button>
                    <button
                        onClick={() => setView("map")}
                        className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors flex items-center gap-1 ${view === "map" ? "bg-[#0B192C] text-white" : "text-slate-500"}`}
                        data-testid="missions-view-map"
                    >
                        <MapIcon size={12} /> Carte
                    </button>
                </div>
            </div>

            {filtered.length === 0 ? (
                <div className="sd-card p-8 text-center text-slate-400 text-sm">
                    Aucune mission ne correspond à ces critères. Élargissez votre recherche ou complétez votre métier principal.
                </div>
            ) : view === "map" ? (
                <MissionsMap
                    missions={filtered}
                    center={profile?.lat && profile?.lng ? { lat: profile.lat, lng: profile.lng } : null}
                    radiusKm={profile?.mobility_km || 25}
                />
            ) : (
                <div className="grid md:grid-cols-2 gap-4">
                    {filtered.map((m) => (
                        <MissionCard key={m.id} m={m} onApply={onApply} />
                    ))}
                </div>
            )}
        </div>
    );
}

function MissionCard({ m, onApply }) {
    const [expanded, setExpanded] = useState(false);
    const [ack, setAck] = useState(false);
    const [transport, setTransport] = useState(false);
    const [msg, setMsg] = useState("");
    const [loading, setLoading] = useState(false);

    const apply = async () => {
        setLoading(true);
        try {
            await api.post("/applications", {
                mission_id: m.id,
                message: msg,
                transport_confirmed: transport,
                acknowledged: ack,
            });
            toast.success("Candidature envoyée");
            setExpanded(false);
            onApply();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        } finally {
            setLoading(false);
        }
    };
    return (
        <div className="sd-card p-5" data-testid={`mission-card-${m.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                    <div className="pill mb-2">{categoryLabel(m.job_category)}</div>
                    <h3 className="font-display text-lg">{m.title}</h3>
                </div>
                <div className={`pill ${m.urgency === "very_urgent" ? "pill-red" : "pill-slate"}`}>
                    {m.urgency}
                </div>
            </div>
            <div className="text-sm text-slate-500 mb-3">
                {m.city} ({m.postal_code}) · {m.date} · {m.start_time}–{m.end_time} · {m.people_count} pers.
                {typeof m.distance_km === "number" && (
                    <span className="ml-2 font-semibold text-[#0B192C]">
                        · {m.distance_km.toFixed(1)} km
                    </span>
                )}
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
                <span className="pill">{m.professional_status_accepted?.join(", ")}</span>
                <span className="pill pill-gold">{fmtEUR(m.hourly_gross)}/h HT</span>
                {m.meal_provided && <span className="pill pill-green">Repas fourni</span>}
                {m.kms_reimbursed && <span className="pill pill-green">Km remboursés</span>}
                {typeof m.active_applications === "number" && (
                    <span
                        className={`pill text-[11px] ${
                            m.active_applications >= 5 ? "pill-red" :
                            m.active_applications >= 3 ? "pill-gold" : "pill-slate"
                        }`}
                        data-testid={`mission-applicants-${m.id}`}
                    >
                        👥 {m.active_applications}/7 candidats
                    </span>
                )}
            </div>
            {!expanded ? (
                <button
                    onClick={() => setExpanded(true)}
                    className="btn-gold w-full text-sm"
                    data-testid={`apply-btn-${m.id}`}
                >
                    Postuler
                </button>
            ) : (
                <div className="space-y-2 mt-2">
                    <textarea
                        rows={2}
                        placeholder="Message optionnel"
                        value={msg}
                        onChange={(e) => setMsg(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                    />
                    <label className="flex items-start gap-2 text-xs text-slate-600">
                        <input type="checkbox" checked={transport} onChange={(e) => setTransport(e.target.checked)} />
                        Je confirme mon moyen de transport
                    </label>
                    <label className="flex items-start gap-2 text-xs text-slate-600">
                        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                            data-testid={`ack-cb-${m.id}`} />
                        Je confirme avoir pris connaissance des horaires, du lieu, des missions et de la rémunération.
                    </label>
                    <div className="flex gap-2">
                        <button onClick={() => setExpanded(false)} className="btn-ghost-navy text-sm flex-1">
                            Annuler
                        </button>
                        <button
                            disabled={!ack || loading}
                            onClick={apply}
                            className="btn-gold text-sm flex-1 disabled:opacity-40"
                            data-testid={`confirm-apply-${m.id}`}
                        >
                            {loading ? "..." : "Envoyer"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function CandidateApplicationsPanel({ apps, onChange }) {
    const [tsForApp, setTsForApp] = useState(null);
    const [timesheets, setTimesheets] = useState({}); // application_id -> timesheet
    const todayISO = new Date().toISOString().slice(0, 10);
    useEffect(() => {
        api.get("/timesheets/mine")
            .then((r) => {
                const map = {};
                (r.data || []).forEach((t) => { map[t.application_id] = t; });
                setTimesheets(map);
            })
            .catch(() => setTimesheets({}));
    }, [apps]);

    const STATUS_FR = {
        selected: "Sélectionné", rejected: "Refusé",
        pending: "En attente", preselected: "Présélectionné", withdrawn: "Retiré",
    };
    const TS_FR = {
        pending_client: "En attente client",
        counter_proposed: "Contre-proposition",
        validated: "Validé",
    };

    const fmtFR = (iso) => {
        if (!iso) return "";
        try { return new Date(iso).toLocaleDateString("fr-FR"); } catch (_) { return iso; }
    };

    // Feb 2026 pivot J+10 — display the scheduled payout date for the pro
    // instead of a vague "En attente" once the timesheet is validated but
    // the client hasn't yet been debited.
    const payoutLabel = (ts) => {
        if (!ts || ts.status !== "validated") return null;
        const st = ts.stripe_transfer_status;
        if (st === "deferred_j10") {
            const d = ts.expected_payout_at;
            return d
                ? `💶 Virement prévu le ${fmtFR(d)}`
                : "💶 Virement prévu à la clôture du cycle J+10";
        }
        if (st === "created") return "💶 Virement envoyé";
        if (st === "paid" || st === "succeeded") return "💶 Virement effectué";
        if (st === "failed") return "⚠️ Virement en échec — support prévenu";
        return null;
    };
    return (
        <div className="sd-card p-6">
            <div className="overline mb-3">Mes candidatures</div>
            <div className="divide-y divide-slate-100">
                {apps.length === 0 && (
                    <div className="py-6 text-sm text-slate-400">Aucune candidature envoyée.</div>
                )}
                {apps.map((a) => {
                    const isSelected = a.status === "selected";
                    const missionDate = a.mission?.date || "9999-12-31";
                    const missionPast = missionDate <= todayISO;
                    const ts = timesheets[a.id];
                    return (
                        <div key={a.id} className="py-3">
                            <div className="flex justify-between items-center gap-3 flex-wrap">
                                <div>
                                    <div className="font-semibold">{a.mission?.title}</div>
                                    <div className="text-xs text-slate-500">
                                        {a.mission?.city} · {a.mission?.date}
                                    </div>
                                    {ts && (
                                        <div className="text-xs text-blue-600 mt-1">
                                            Relevé : <b>{TS_FR[ts.status] || ts.status}</b>
                                            {ts.status === "validated" && ts.final_invoice_pdf_path && (
                                                <>
                                                    {" — "}
                                                    <a
                                                        href="#"
                                                        onClick={async (e) => {
                                                            e.preventDefault();
                                                            try {
                                                                const r = await api.get(`/timesheets/${ts.id}/pdf`, { responseType: "blob" });
                                                                const url = URL.createObjectURL(r.data);
                                                                window.open(url, "_blank");
                                                                setTimeout(() => URL.revokeObjectURL(url), 60000);
                                                            } catch (_) {}
                                                        }}
                                                        className="underline"
                                                    >
                                                        Voir le PDF
                                                    </a>
                                                </>
                                            )}
                                            {payoutLabel(ts) && (
                                                <div className="text-emerald-700 mt-0.5" data-testid={`payout-status-${ts.id}`}>
                                                    {payoutLabel(ts)}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 flex-wrap justify-end">
                                    <span className={`pill ${
                                        isSelected ? "pill-green" :
                                        a.status === "rejected" ? "pill-red" :
                                        "pill-slate"
                                    }`}>
                                        {STATUS_FR[a.status] || a.status}
                                    </span>
                                    {isSelected && missionPast && !ts && (
                                        <button
                                            onClick={() => setTsForApp(a)}
                                            className="btn-gold text-xs"
                                            data-testid={`open-declare-hours-${a.id}`}
                                        >
                                            ⏱️ Déclarer mes heures
                                        </button>
                                    )}
                                </div>
                            </div>
                            {isSelected && a.mission?.id && (
                                <CandidateMissionChatToggle
                                    missionId={a.mission.id}
                                    missionTitle={a.mission?.title}
                                />
                            )}
                            {isSelected && missionDate <= todayISO && !ts && (
                                <MissionLiveTracking
                                    applicationId={a.id}
                                    missionTitle={a.mission?.title || "Mission"}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
            <CandidateTimesheetModal
                open={!!tsForApp}
                application={tsForApp}
                onClose={() => setTsForApp(null)}
                onSubmitted={() => { onChange(); setTsForApp(null); }}
            />
        </div>
    );
}

function CandidateMissionChatToggle({ missionId, missionTitle }) {
    const [open, setOpen] = useState(false);
    const [unread, setUnread] = useState(0);
    useEffect(() => {
        const load = () => api.get(`/missions/${missionId}/messages/unread-count`)
            .then((r) => setUnread(r.data?.count || 0))
            .catch(() => {});
        load();
        const iv = setInterval(load, 20000);
        return () => clearInterval(iv);
    }, [missionId]);
    return (
        <div className="mt-3">
            <button
                onClick={() => { setOpen((v) => !v); if (!open) setUnread(0); }}
                className="btn-ghost-navy text-xs inline-flex items-center gap-2"
                data-testid={`toggle-mission-chat-${missionId}`}
            >
                💬 {open ? "Masquer la messagerie" : "Messagerie mission"}
                {unread > 0 && !open && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-[#F5A623] text-[#0B192C] text-[10px] font-bold px-1">
                        {unread}
                    </span>
                )}
            </button>
            {open && (
                <div className="mt-3">
                    <MissionChat
                        missionId={missionId}
                        myRole="candidate"
                        headerLabel={`Mission — ${missionTitle || ""}`}
                        otherPartyName="le client"
                    />
                </div>
            )}
        </div>
    );
}




function CandidateInvoicesPanel() {
    const [invs, setInvs] = React.useState([]);
    const [loading, setLoading] = React.useState(true);

    const refresh = async () => {
        setLoading(true);
        try {
            const r = await api.get("/candidates/me/invoices");
            setInvs(r.data || []);
        } catch (_) { setInvs([]); }
        finally { setLoading(false); }
    };
    React.useEffect(() => { refresh(); }, []);

    const download = async (inv) => {
        try {
            const r = await api.get(`/candidates/me/invoices/${inv.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${inv.invoice_number}.pdf`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } catch (_) {
            toast.error("Erreur de téléchargement");
        }
    };

    const fmt = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
    const totalTTC = invs.reduce((s, i) => s + (i.total_ttc || 0), 0);

    return (
        <section className="sd-card p-6" data-testid="candidate-invoices-panel">
            <div className="flex justify-between items-start flex-wrap gap-3 mb-5">
                <div>
                    <div className="overline">Comptabilité</div>
                    <h2 className="font-display text-xl mt-1">Factures Renforteo</h2>
                    <p className="text-xs text-slate-500 mt-1">
                        Factures de la commission Renforteo (mise en relation) prélevée sur vos rémunérations d'auto-entrepreneur. Téléchargez-les pour votre comptabilité.
                    </p>
                </div>
                <div className="text-right">
                    <div className="text-xs text-slate-500">Total facturé TTC</div>
                    <div className="font-display text-xl font-black text-[#0B192C]">{fmt(totalTTC)}</div>
                </div>
            </div>
            {loading && <div className="text-sm text-slate-400 py-8 text-center">Chargement…</div>}
            {!loading && invs.length === 0 && (
                <div className="text-sm text-slate-400 py-10 text-center" data-testid="candidate-invoices-empty">
                    Aucune facture pour l'instant. Elles apparaîtront ici automatiquement après chaque contrat signé.
                </div>
            )}
            {!loading && invs.length > 0 && (
                <div className="divide-y divide-slate-100">
                    {invs.map((inv) => (
                        <div key={inv.id} className="py-3 grid md:grid-cols-[1fr_120px_120px_120px_100px] gap-3 items-center text-sm"
                             data-testid={`invoice-row-${inv.id}`}>
                            <div>
                                <div className="font-semibold text-[#0B192C]">{inv.invoice_number}</div>
                                <div className="text-xs text-slate-500">
                                    Contrat {inv.contract_number} · {inv.mission_title}
                                </div>
                                <div className="text-[10px] text-slate-400">
                                    {new Date(inv.created_at).toLocaleDateString("fr-FR")}
                                </div>
                            </div>
                            <div className="text-right text-xs">
                                <div className="text-slate-500">Commission</div>
                                <div className="font-semibold">{inv.commission_pct}%</div>
                            </div>
                            <div className="text-right text-xs">
                                <div className="text-slate-500">HT</div>
                                <div className="font-semibold">{fmt(inv.amount_ht)}</div>
                            </div>
                            <div className="text-right text-xs">
                                <div className="text-slate-500">TTC</div>
                                <div className="font-semibold text-[#0B192C]">{fmt(inv.total_ttc)}</div>
                            </div>
                            <button
                                onClick={() => download(inv)}
                                className="btn-gold text-xs"
                                data-testid={`invoice-download-${inv.id}`}
                            >
                                Télécharger
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

